import { Search } from 'lucide-react';
import type { ChangeEvent, ReactNode } from 'react';

interface SearchFilterBarProps {
  /** Current search text. */
  value: string;
  /** Raw input change handler — receives the native change event verbatim. */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Placeholder text for the search input. */
  placeholder: string;
  /**
   * Optional filter controls rendered to the right of the search box (e.g.
   * status / type dropdowns). Sites with no extra filters pass nothing and
   * render exactly the search input.
   */
  children?: ReactNode;
}

/**
 * Filters row used at the top of the Tasks and LMS list "Content Card"s: a
 * full-width search input (with leading Search icon) optionally followed by
 * page-specific filter dropdowns supplied via `children`. Extracted from the
 * byte-identical markup in TasksPage, LmsContentPage and LmsSessionsPage so the
 * styling stays in one place; appearance is unchanged at every call site.
 */
export function SearchFilterBar({ value, onChange, placeholder, children }: SearchFilterBarProps) {
  return (
    <div className="mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        {children}
      </div>
    </div>
  );
}

export default SearchFilterBar;
